// Hooks
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AssignTemplateDialog from 'src/components/UmiBuilder/components/AssignTemplateDialog'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'
import { useNavigationEffect } from 'src/redux/slices/navigation/hooks'
import { getProject } from 'src/redux/slices/project/endpoints'
import { mutationFixedCacheKeys, sendGroupData } from 'src/redux/slices/umibuilder/endpoints'
import { changeArchetypeField } from 'src/redux/slices/umibuilder/reducers'
import {
  isLoading,
  selectGroupData,
  selectSummaryData,
  selectUmiBuilderFromCloud,
} from 'src/redux/slices/umibuilder/selectors'
import { ArchetypeGroup } from 'src/redux/slices/umibuilder/types'

import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import EditIcon from '@material-ui/icons/Edit'
import {
  DataGrid,
  GridCellEditCommitParams,
  GridCellParams,
  GridColumns,
  GridEditRowsModel,
} from '@mui/x-data-grid'

///// API Hooks

///// Grid Table Config
const columns: GridColumns = [
  {
    field: 'primaryField',
    headerName: 'Field',
    hide: true,
  },
  {
    field: 'archetypeID',
    headerName: 'Archetype ID',
    minWidth: 300,
    editable: false,
  },
  {
    field: 'WW',
    headerName: 'WWR',
    description: 'Window-to-Wall Ratio',
    type: 'number',
    minWidth: 140,
    editable: true,
    // min: 0,
    // max: 100,
    valueFormatter: ({ value }) => `${value}%`,
    renderCell: ({ value }) => (
      <Tooltip title='Double-click to edit'>
        <Typography>{`${value}%`}</Typography>
      </Tooltip>
    ),
  },
  {
    field: 'template',
    headerName: 'Template',
    minWidth: 400,
    flex: 1,
    editable: false,
    renderCell: ({ value }) => (
      <>
        <Button
          style={{ margin: '1rem' }}
          size='small'
          variant='outlined'
          color='primary'
          startIcon={<EditIcon />}
        >
          Edit
        </Button>
        <Typography variant='overline'>
          {value === 'N/A' || !value ? <>N/A (shading)</> : <>{value}</>}
        </Typography>
      </>
    ),
  },
]

type TemplateAssignmentContext = {
  archetypeID: string
  primaryField: string
}

const AssignTemplatesPage: React.FC = () => {
  const dispatch = useDispatch()
  const loading = useSelector(isLoading)
  const allGroups = useSelector(selectGroupData)
  const summaryData = useSelector(selectSummaryData)
  const { data: project, isLoading: isLoadingProject } = getProject.useQuery('')
  const fromCloud = useSelector(selectUmiBuilderFromCloud)

  const [openDialog, setOpenDialog] = useState(false)
  const [templateAssignmentContext, setTemplateAssignmentContext] =
    useState<TemplateAssignmentContext | null>(null) // simple context for the last clicked template

  const [trigger] = sendGroupData.useMutation({
    fixedCacheKey: mutationFixedCacheKeys.sendGroupData,
  })

  // Change to context layers page when final archetype assignment is complete
  useNavigationEffect({ destination: 'context', condition: summaryData })

  // Flattened group data
  const data = useMemo(
    () =>
      allGroups
        ?.map(({ groups }) => groups)
        .flat()
        .map((group) => ({
          ...group,
          id: `${group.primaryField}/UBEMIO/${group.archetypeID}`,
        })) ?? [],
    [allGroups],
  )

  // Update archetype group data in backend when values change
  // TODO: maybe only send on confirm?
  useEffect(() => {
    trigger({ groups: allGroups, project_id: project?.id, fromCloud })
  }, [allGroups, project, fromCloud])

  // Trigger async processing
  const assignBuildingsToArchetypes = useCallback(() => {
    const body = {
      fromCloud,
      projectId: project?.id,
    }
    dispatch(startAsyncTask({ endpoint: AsyncTask.assignBuildingsToArchetypes, body }))
  }, [dispatch, project, fromCloud])

  // Handle confirming a cell
  const handleCommitCell = useCallback(
    (model: GridCellEditCommitParams) => {
      const [field, archetypeID] = (model.id as string).split('/UBEMIO/')
      const { field: k, value: v } = model
      const key = k as keyof ArchetypeGroup
      const value = v as string | number | boolean | null | undefined
      dispatch(changeArchetypeField({ field, archetypeID, key, value }))
    },
    [dispatch],
  )

  // Editing model allows rejecting edits etc
  const [editingModel, setEditingModel] = useState({})

  // Validate WWR is 0-100 (#TODO: show better error when invalid)
  const handleEditModel = useCallback(
    (model: GridEditRowsModel) => {
      const newModel = { ...model }
      Object.keys(newModel).forEach((id) => {
        if (newModel[id].WW) {
          const testValue: number = newModel[id].WW.value as number
          newModel[id].WW = {
            ...newModel[id].WW,
            error: testValue < 0 || testValue > 100,
          }
        }
      })
      setEditingModel(newModel)
    },
    [setEditingModel],
  )

  const handleOpenDialog = useCallback(
    ({ row, field }: GridCellParams) => {
      const { primaryField, archetypeID } = row as TemplateAssignmentContext
      if (field === 'template') {
        setTemplateAssignmentContext({
          archetypeID,
          primaryField,
        })
        setOpenDialog(true)
      }
    },
    [setTemplateAssignmentContext, setOpenDialog],
  )

  return (
    <Grid container justifyContent='center' spacing={2} direction='column'>
      <div style={{ display: 'flex', height: '100%' }}>
        <AssignTemplateDialog
          open={openDialog}
          setOpen={setOpenDialog}
          archetypeID={templateAssignmentContext?.archetypeID}
          primaryField={templateAssignmentContext?.primaryField}
        />
        <div style={{ flexGrow: 1 }}>
          <DataGrid
            rows={data}
            columns={columns}
            pageSize={25}
            autoHeight
            onEditRowsModelChange={handleEditModel}
            onCellEditCommit={handleCommitCell}
            editRowsModel={editingModel}
            onCellClick={handleOpenDialog}
            // density="compact"
          />
        </div>
      </div>
      <Button
        style={{ marginBottom: '3rem' }}
        variant='contained'
        color='primary'
        onClick={assignBuildingsToArchetypes}
        disabled={loading}
      >
        Confirm
      </Button>
    </Grid>
  )
}

export default AssignTemplatesPage
